import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Mail,
  Phone,
  Eye,
  CheckCircle,
  X,
  AlertCircle,
  DollarSign,
  Users,
  Plus,
} from "lucide-react";
import { useAuth } from "../App";
import {
  financeAPI,
  trainersAPI,
  sessionsAPI,
  studentsAPI,
  notificationsAPI,
} from "../services/api";
import { useApp } from "../contexts/AppContext";
import { useTheme } from "../contexts/ThemeContext";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import moment from "moment";

export const TrainerDetailsPage = () => {
  const { colors } = useTheme();

  const calculateDateRange = (salaryDate) => {
    const salaryDay = parseInt(salaryDate || 1);

    // Start date is the day AFTER last salary date (e.g. if salary is 15th, start is 16th of previous month)
    const startDate = moment()
      .subtract(1, "month")
      .date(salaryDay + 1)
      .startOf('day');

    // End date is exactly the CURRENT salary date (e.g. if salary is 15th, end is 15th of current month)
    const endDate = moment(startDate)
      .add(1, "month")
      .subtract(1, "day")
      .endOf('day');

    return {
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    };
  };

  const { trainerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganizationId, userId } = useApp();
  const { user } = useAuth();

  const organizationId =
    location.state?.organizationId || selectedOrganizationId;

  const [trainer, setTrainer] = useState(location.state?.trainer || null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(!trainer);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sessions");
  const [error, setError] = useState(null);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [salaryData, setSalaryData] = useState({
    amount: "",
    date: moment().format("YYYY-MM-DD"),
    description: "",
  });
  const [salaryError, setSalaryError] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    if (!trainer?.salaryDate) {
      return {
        startDate: moment().startOf("week").format("YYYY-MM-DD"),
        endDate: moment().endOf("week").format("YYYY-MM-DD"),
      };
    }

    return calculateDateRange(trainer.salaryDate);
  });

  const fetchTrainerData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await trainersAPI.getById(organizationId, trainerId);
      const trainerData = response.data.data;
      setTrainer(trainerData);

      console.log("trainerData:", trainerData);
      setDateRange(calculateDateRange(trainerData.salaryDate));

      setError(null);
    } catch (err) {
      console.error("Error fetching trainer:", err);
      setError("Eğitmen verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [organizationId, trainerId]);

  const fetchTrainerSessions = useCallback(async () => {
    try {
      setSessionsLoading(true);
      const [sessionsRes, balancesRes] = await Promise.all([
        sessionsAPI.getByTrainer(
          organizationId,
          trainerId,
          dateRange.startDate,
          dateRange.endDate,
        ),
        financeAPI.getTrainerBalances(
          organizationId,
          dateRange.startDate,
          dateRange.endDate
        )
      ]);
      setSessions(sessionsRes.data.data || []);
      setTrainerBalances(balancesRes.data.data || []);
    } catch (err) {
      console.error("Error fetching sessions/balances:", err);
    } finally {
      setSessionsLoading(false);
    }
  }, [organizationId, trainerId, dateRange]);

  const fetchAssignedStudents = useCallback(async () => {
    try {
      setStudentsLoading(true);
      const response = await studentsAPI.getAll(organizationId);
      const allStudents = response.data.data || [];
      // Filter students assigned to this trainer
      const assigned = allStudents.filter(
        (s) => s.mainTrainerId === trainerId
      );
      setAssignedStudents(assigned);
    } catch (err) {
      console.error("Error fetching assigned students:", err);
    } finally {
      setStudentsLoading(false);
    }
  }, [organizationId, trainerId]);

  // ─── Calculation Helpers ───────────────────────────────────────────────────

  // Calculate trainer earning for a session using CURRENT trainer commission percentages
  const getEffectiveSessionCommission = useCallback((session) => {
    const isGroupByName = (session.sessionTypeName || "").toLowerCase().includes("grup") ||
      (session.packageName || "").toLowerCase().includes("grup") ||
      (session.sessionTypeName || "").toLowerCase().includes("group") ||
      (session.packageName || "").toLowerCase().includes("group");
    const effectiveIsGroup = session.isGroupSession || !!session.sessionTypeIsGroup || isGroupByName;

    let commissionPercentage;
    if (effectiveIsGroup) {
      const gComm = Number(trainer?.groupCommissionPercentage ?? trainer?.trainerCommissionPercentage ?? session?.trainerCommissionPercentage);
      commissionPercentage = gComm > 0 ? gComm : 30;
    } else {
      const pComm = Number(trainer?.privateCommissionPercentage ?? trainer?.trainerCommissionPercentage ?? session?.trainerCommissionPercentage);
      commissionPercentage = pComm > 0 ? pComm : 40;
    }
    return parseFloat(commissionPercentage);
  }, [trainer]);

  const calculateTrainerEarning = useCallback((session) => {
    if (session.trainerEarningAmount) {
      return parseFloat(session.trainerEarningAmount);
    }
    else {
      const studentPayment = parseFloat(session.studentPaymentAmount || 0);
      const commissionPercentage = getEffectiveSessionCommission(session);
      return (studentPayment * commissionPercentage) / 100;
    }
  }, [getEffectiveSessionCommission]);

  const getGroupedSessions = useCallback(() => {
    const grouped = {};
    sessions.forEach(session => {
      const key = `${session.sessionDate}_${session.startTime}_${session.endTime}`;
      if (!grouped[key]) {
        grouped[key] = {
          ...session,
          studentNames: [session.studentName],
          totalPayment: parseFloat(session.studentPaymentAmount || 0),
          totalEarning: calculateTrainerEarning(session)
        };
      } else {
        if (!grouped[key].studentNames.includes(session.studentName)) {
          grouped[key].studentNames.push(session.studentName);
        }
        grouped[key].totalPayment += parseFloat(session.studentPaymentAmount || 0);
        grouped[key].totalEarning += calculateTrainerEarning(session);
      }
    });
    return Object.values(grouped).sort((a, b) => {
      const dateA = new Date(a.sessionDate);
      const dateB = new Date(b.sessionDate);
      if (dateA.getTime() !== dateB.getTime()) return dateB - dateA;
      return moment(b.startTime).diff(moment(a.startTime));
    });
  }, [sessions, calculateTrainerEarning]);

  const [trainerBalances, setTrainerBalances] = useState([]);

  const calculateStats = useCallback(() => {
    // Backend returns one balance entry per (org, trainer) pair. When viewing in
    // "All Organizations" mode, this trainer can have multiple entries — one for
    // each org they belong to. We need to SUM them, not pick the first.
    const myBalances = trainerBalances.filter(b => b.id === trainerId);

    let balance = null;
    if (myBalances.length > 0) {
      const isAll = !organizationId || organizationId === 'all';
      if (isAll && myBalances.length > 1) {
        // Aggregate across orgs
        balance = myBalances.reduce(
          (acc, b) => ({
            id: trainerId,
            name: b.name || acc.name,
            organizationId: 'all',
            earned: (acc.earned || 0) + (Number(b.earned) || 0),
            earnedPrivate: (acc.earnedPrivate || 0) + (Number(b.earnedPrivate) || 0),
            earnedGroup: (acc.earnedGroup || 0) + (Number(b.earnedGroup) || 0),
            revenue: (acc.revenue || 0) + (Number(b.revenue) || 0),
            revenuePrivate: (acc.revenuePrivate || 0) + (Number(b.revenuePrivate) || 0),
            revenueGroup: (acc.revenueGroup || 0) + (Number(b.revenueGroup) || 0),
            paid: (acc.paid || 0) + (Number(b.paid) || 0),
            pending: (acc.pending || 0) + (Number(b.pending) || 0),
          }),
          { id: trainerId, name: '', organizationId: 'all', earned: 0, earnedPrivate: 0, earnedGroup: 0, revenue: 0, revenuePrivate: 0, revenueGroup: 0, paid: 0, pending: 0 },
        );
      } else {
        // Prefer exact org match, fall back to any entry for this trainer
        balance = myBalances.find(b => b.organizationId === organizationId) || myBalances[0];
      }
    }

    const grouped = getGroupedSessions();
    const completedSessionsCount = grouped.filter(s => s.status === "completed").length;

    if (balance) {
      const pCommission = Number(trainer?.privateCommissionPercentage || 40);
      const gCommission = Number(trainer?.groupCommissionPercentage || 30);

      return {
        completedSessions: completedSessionsCount,
        totalRevenue: (balance.revenue || 0).toFixed(2),
        trainerRevenue: (balance.earned || 0).toFixed(2),
        privateRevenue: (balance.earnedPrivate || 0).toFixed(2),
        groupRevenue: (balance.earnedGroup || 0).toFixed(2),
        privateCommission: pCommission,
        groupCommission: gCommission,
        paid: (balance.paid || 0).toFixed(2),
        pending: (balance.pending || 0).toFixed(2)
      };
    }

    // Fallback to 0s if balance not yet loaded
    return {
      completedSessions: completedSessionsCount,
      totalRevenue: "0.00",
      trainerRevenue: "0.00",
      privateRevenue: "0.00",
      groupRevenue: "0.00",
      privateCommission: trainer?.privateCommissionPercentage || 0,
      groupCommission: trainer?.groupCommissionPercentage || 0
    };
  }, [trainerBalances, trainerId, organizationId, trainer, getGroupedSessions]);

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (organizationId && trainerId) {
      fetchTrainerData();
      fetchAssignedStudents();
    } else if (!organizationId) {
      setError("Organizasyon seçilmedi");
      setLoading(false);
    }
  }, [organizationId, trainerId, fetchTrainerData, fetchAssignedStudents]);

  useEffect(() => {
    if (organizationId && trainerId && trainer) {
      fetchTrainerSessions();
    }
  }, [dateRange, organizationId, trainerId, trainer, fetchTrainerSessions]);

  useEffect(() => {
    if (showSalaryModal) {
      const stats = calculateStats();
      setSalaryData((prev) => ({
        ...prev,
        amount: stats.trainerRevenue,
        date: moment().format("YYYY-MM-DD"),
      }));
    }
  }, [showSalaryModal, calculateStats]);

  const handleSessionView = (session) => {
    navigate(`/sessions/${session.id}`, {
      state: {
        session,
        organizationId,
        returnPath: `/trainers/${trainerId}`,
      },
    });
  };

  const getSessionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case "cancelled":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
      case "scheduled":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
      case "no-show":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const getSessionStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Tamamlandı";
      case "cancelled":
        return "İptal Edildi";
      case "scheduled":
        return "Planlandı";
      case "no-show":
        return "Gelmedi";
      default:
        return status;
    }
  };



  const handleSalarySubmit = async () => {
    try {
      const calculatedAmount = parseFloat(calculateStats().trainerRevenue);
      const val = parseFloat(salaryData.amount);
      const finalAmount = !isNaN(val) ? val : calculatedAmount;

      if (finalAmount <= 0) {
        console.error("Validation failed: Amount must be greater than 0");
        setSalaryError("Lütfen 0'dan büyük geçerli bir tutar giriniz.");
        return;
      }

      console.log("Adding trainer salary:", {
        trainerId,
        organizationId,
        amount: finalAmount,
        date: salaryData.date,
        description: salaryData.description,
        dateRange,
      });

      await financeAPI.createTransaction(organizationId, {
        category: "trainer_salary",
        type: "outcome",
        amount: finalAmount,
        transactionDate: salaryData.date,
        description:
          salaryData.description || `Eğitmen Maaş Ödemesi - ${trainer.name}`,
        trainerId: trainerId,
      });

      setSalaryData({
        amount: "",
        date: moment().format("YYYY-MM-DD"),
        description: "",
      });
      setSalaryError(null); // Clear error on success

      // TRIGGER CUSTOM NOTIFICATION FROM FRONTEND
      try {
        const formattedAmount = new Intl.NumberFormat("tr-TR", {
          style: "currency",
          currency: "TRY",
        }).format(Number(finalAmount));
        const creatorName = user?.name || user?.email || "Bilinmeyen Kullanıcı";
        await notificationsAPI.create(selectedOrganizationId, {
          title: "Eğitmen Ödemesi Yapıldı",
          body: `${trainer.name} eğitmenin maaşı ödendi (${formattedAmount}) (Oluşturan: ${creatorName})`,
          receivers: ["role:super_admin", "role:organization_manager", userId],
          type: "payment",
          metadata: { trainerId: trainer.id, amount: finalAmount },
        });
        console.log("Frontend notification sent successfully");
      } catch (notifError) {
        console.error("Failed to send frontend notification:", notifError);
        // We don't block the success flow if notification fails, but we log it.
      }

      setShowSalaryModal(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Error adding trainer salary:", error);
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Maaş eklenirken bir hata oluştu";
      setSalaryError(msg);
    }
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !trainer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-96">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>
          Eğitmen Yüklenirken Hata
        </h3>
        <p className={`${colors.textMuted} mb-4`}>
          {error || "Eğitmen bulunamadı"}
        </p>
        <button
          onClick={() => navigate("/trainers")}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
        >
          Eğitmenlere Dön
        </button>
      </div>
    );
  }

  return (
    <div className={`${colors.bgPrimary} px-4 py-4 pb-12`}>
      {/* Rounded card wraps header + stats */}
      <div
        className={`${colors.cardBg} ${colors.cardBorder} border rounded-2xl shadow-xl overflow-hidden mb-4`}
      >
        {/* Compact Unified Header */}
        <div className={`border-b ${colors.border} px-4 sm:px-6 py-3`}>
          {/* Row 1: Back + Avatar + Name/Contact */}
          <div className="flex items-center gap-3 mb-2 lg:mb-0 lg:hidden">
            <button
              onClick={() => navigate("/trainers")}
              className={`p-2 ${colors.bgHover} rounded-xl transition-all shrink-0`}
            >
              <ArrowLeft className={`w-5 h-5 ${colors.textTertiary}`} />
            </button>

            {trainer.profilePicture ? (
              <img
                src={trainer.profilePicture}
                alt={trainer.name}
                className="w-9 h-9 rounded-full object-cover shrink-0"
              />
            ) : (
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                style={{ backgroundColor: trainer.color || "#6B7280" }}
              >
                {trainer.name
                  ?.split(" ")
                  .map((n) => n.charAt(0))
                  .join("")
                  .slice(0, 2)}
              </div>
            )}

            <div className="flex flex-col justify-center leading-tight min-w-0 flex-1">
              <h1
                className={`text-sm font-bold ${colors.textPrimary} leading-snug`}
              >
                {trainer.name}
              </h1>
              <div
                className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] ${colors.textMuted} mt-0.5`}
              >
                {trainer.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate max-w-[130px]">
                      {trainer.email}
                    </span>
                  </span>
                )}
                {trainer.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-2.5 h-2.5 shrink-0" />
                    {trainer.phone}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Row 2 on mobile (all inline on desktop): Tabs + Action */}
          <div className="flex items-center gap-2">
            {/* Back + Avatar + Name — desktop only, inline */}
            <div className="hidden lg:flex items-center gap-3 shrink-0 mr-2">
              <button
                onClick={() => navigate("/trainers")}
                className={`p-2 ${colors.bgHover} rounded-xl transition-all`}
              >
                <ArrowLeft className={`w-5 h-5 ${colors.textTertiary}`} />
              </button>
              {trainer.profilePicture ? (
                <img
                  src={trainer.profilePicture}
                  alt={trainer.name}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: trainer.color || "#6B7280" }}
                >
                  {trainer.name
                    ?.split(" ")
                    .map((n) => n.charAt(0))
                    .join("")
                    .slice(0, 2)}
                </div>
              )}
              <div className="flex flex-col justify-center leading-tight min-w-0">
                <h1
                  className={`text-base font-bold ${colors.textPrimary} truncate max-w-[200px]`}
                >
                  {trainer.name}
                </h1>
                <div
                  className={`flex items-center gap-3 text-xs ${colors.textMuted}`}
                >
                  {trainer.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      <span className="truncate max-w-[150px]">
                        {trainer.email}
                      </span>
                    </span>
                  )}
                  {trainer.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {trainer.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs — scrollable */}
            <div
              className={`flex items-center gap-1 overflow-x-auto hide-scrollbar flex-1 ${colors.bgTertiary} rounded-xl p-1`}
            >
              {[
                { id: "sessions", label: "Seanslar" },
                { id: "working-hours", label: "Çalışma Saatleri" },
                { id: "students", label: "Öğrenciler" },
                { id: "details", label: "Detaylar" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${activeTab === tab.id
                    ? `${colors.cardBg} ${colors.textPrimary} shadow-sm`
                    : `${colors.textSecondary} hover:${colors.textPrimary}`
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Salary Button — only on sessions tab */}
            {activeTab === "sessions" && (
              <button
                onClick={() => setShowSalaryModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-xl text-xs font-semibold hover:bg-purple-700 transition-colors shadow-sm shrink-0 whitespace-nowrap"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Eğitmen Maaşı Ekle</span>
                <span className="sm:hidden">Maaş Ekle</span>
              </button>
            )}
          </div>
        </div>

        {/* Sessions Stats + Date Range — compact inline strip */}
        {activeTab === "sessions" && (
          <div
            className={`${colors.cardBg} border-b ${colors.border} px-6 sm:px-8 py-3 flex flex-wrap items-center gap-4`}
          >
            {/* Date pickers */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex flex-col">
                <span
                  className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5`}
                >
                  Başlangıç
                </span>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                  className={`px-2 py-1 border rounded-lg text-xs ${colors.inputBg} ${colors.inputText} ${colors.inputBorder} focus:ring-1 ${colors.inputFocus} focus:border-transparent`}
                />
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5`}
                >
                  Bitiş
                </span>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                  className={`px-2 py-1 border rounded-lg text-xs ${colors.inputBg} ${colors.inputText} ${colors.inputBorder} focus:ring-1 ${colors.inputFocus} focus:border-transparent`}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-white/10" />

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex flex-col">
                <span
                  className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5 flex items-center gap-1`}
                >
                  <CheckCircle className="w-3 h-3 text-green-500" /> Tamamlanan
                  Seanslar
                </span>
                <span
                  className={`text-lg font-black ${colors.textPrimary} leading-none`}
                >
                  {stats.completedSessions}
                </span>
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5 flex items-center gap-1`}
                >
                  <DollarSign className="w-3 h-3 text-blue-500" /> Toplam Gelir
                </span>
                <span
                  className={`text-lg font-black ${colors.textPrimary} leading-none`}
                >
                  ₺{stats.totalRevenue}
                </span>
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-[9px] uppercase font-bold tracking-wider ${colors.textTertiary} mb-0.5 flex items-center gap-1`}
                >
                  <Users className="w-3 h-3 text-purple-500" /> Eğitmen Geliri
                </span>
                <span
                  className={`text-lg font-black text-purple-600 dark:text-purple-400 leading-none`}
                >
                  ₺{stats.trainerRevenue}
                </span>
              </div>

              {/* Commission Breakdown cards - integrated next to total earning */}
              <div className="flex items-center gap-4 bg-zinc-50 dark:bg-zinc-800/30 px-3 py-1.5 rounded-xl border border-zinc-200/50 dark:border-white/5">
                <div className="flex flex-col pr-4 border-r border-zinc-200 dark:border-white/10">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                    <span className={`text-[9px] uppercase font-bold tracking-wider ${colors.textMuted}`}>Bireysel (%{stats.privateCommission})</span>
                  </div>
                  <span className={`text-xs font-black ${colors.textPrimary}`}>₺{stats.privateRevenue}</span>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                    <span className={`text-[9px] uppercase font-bold tracking-wider ${colors.textMuted}`}>Grup (%{stats.groupCommission})</span>
                  </div>
                  <span className={`text-xs font-black ${colors.textPrimary}`}>₺{stats.groupRevenue}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="p-4 sm:p-6 pt-6">
          {/* Sessions Tab */}
          {activeTab === "sessions" && (
            <div className="space-y-6">
              <div
                className={`${colors.cardBg} backdrop-blur-sm rounded-2xl border ${colors.cardBorder} shadow-sm`}
              >
                <div className="overflow-x-auto">
                  {sessionsLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : sessions.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar
                        className={`w-12 h-12 ${colors.textMuted} mx-auto mb-4`}
                      />
                      <h3
                        className={`text-lg font-medium ${colors.textPrimary} mb-2`}
                      >
                        Seans Bulunamadı
                      </h3>
                      <p className={colors.textMuted}>
                        Seçilen tarih aralığında seans bulunamadı.
                      </p>
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className={colors.tableHeader}>
                        <tr>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Öğrenci
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Durum
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Tarih
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Saat
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Paket Adı
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Ders Tipi
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Session Price - Ders Fiyatı
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            Eğitmen Kazanç
                          </th>
                          <th
                            className={`px-6 py-3 text-left text-xs font-medium ${colors.textMuted} uppercase tracking-wider`}
                          >
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody className={`${colors.tableDivide} divide-y`}>
                        {getGroupedSessions().map((session, idx) => (
                          <tr key={session.id || idx} className={colors.tableRowHover}>
                            <td className="px-6 py-4">
                              <div className="flex flex-col">
                                {session.studentNames?.map((name, i) => (
                                  <span key={i} className={`text-sm font-medium ${colors.textPrimary} ${i > 0 ? 'mt-0.5 border-t border-zinc-800/10 pt-0.5' : ''}`}>
                                    {name}
                                  </span>
                                ))}
                                {(!session.studentNames || session.studentNames.length === 0) && (
                                  <span className={`text-sm font-medium ${colors.textPrimary}`}>{session.studentName}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border ${getSessionStatusColor(session.status)}`}
                              >
                                {getSessionStatusText(session.status)}
                              </span>
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}
                            >
                              {moment(session.sessionDate).format("DD/MM/YYYY")}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}
                            >
                              {moment(session.startTime).format("HH:mm")} -{" "}
                              {moment(session.endTime).format("HH:mm")}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textPrimary}`}
                            >
                              <span className="text-zinc-500 text-xs font-bold uppercase tracking-widest">{session.packageName || "-"}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded border ${session.isGroupSession || (session.sessionTypeName || "").toLowerCase().includes("grup") || (session.packageName || "").toLowerCase().includes("grup")
                                  ? "bg-purple-500/5 text-purple-500 border-purple-500/20"
                                  : "bg-blue-500/5 text-blue-500 border-blue-500/20"
                                  }`}
                              >
                                {session.isGroupSession || (session.sessionTypeName || "").toLowerCase().includes("grup") || (session.sessionTypeName || "").toLowerCase().includes("group")
                                  ? `Grup (x${session.studentNames?.length || 1})`
                                  : `Bireysel`}
                              </span>
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm font-bold ${colors.textPrimary}`}
                            >
                              ₺{parseFloat(session.totalPayment || session.studentPaymentAmount || 0).toFixed(2)}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm font-bold text-emerald-500`}
                            >
                              ₺{(session.totalEarning || calculateTrainerEarning(session)).toFixed(2)}
                            </td>
                            <td
                              className={`px-6 py-4 whitespace-nowrap text-sm ${colors.textMuted}`}
                            >
                              <button
                                onClick={() => handleSessionView(session)}
                                className="p-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
                                title="Seans Detaylarını Görüntüle"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Working Hours View Tab */}
          {activeTab === "working-hours" && (
            <div
              className={`${colors.cardBg} backdrop-blur-sm rounded-lg border ${colors.cardBorder} p-6`}
            >
              <h3 className={`text-lg font-medium ${colors.textPrimary} mb-4`}>
                Çalışma Saatleri
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(trainer.preferredHours || {}).map(
                  ([day, hours]) => {
                    const dayNames = {
                      monday: "Pazartesi",
                      tuesday: "Salı",
                      wednesday: "Çarşamba",
                      thursday: "Perşembe",
                      friday: "Cuma",
                      saturday: "Cumartesi",
                      sunday: "Pazar",
                    };

                    return (
                      <div
                        key={day}
                        className={`${colors.bgTertiary} rounded-lg p-4 border ${colors.border}`}
                      >
                        <h4
                          className={`font-medium ${colors.textPrimary} mb-2`}
                        >
                          {dayNames[day]}
                        </h4>
                        <div className={`text-sm ${colors.textTertiary}`}>
                          {hours.start && hours.end
                            ? `${hours.start}:00 - ${hours.end}:00`
                            : "Ayarlanmadı"}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          {/* Students Tab */}
          {activeTab === "students" && (
            <div className="space-y-6">
              <div className={`${colors.cardBg} backdrop-blur-sm rounded-2xl border ${colors.cardBorder} shadow-sm overflow-hidden`}>
                {studentsLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <LoadingSpinner />
                  </div>
                ) : assignedStudents.length === 0 ? (
                  <div className="text-center py-20">
                    <Users className={`w-12 h-12 ${colors.textMuted} mx-auto mb-4`} />
                    <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>Öğrenci Bulunamadı</h3>
                    <p className={colors.textMuted}>Bu eğitmene atanmış aktif öğrenci kaydı bulunamadı.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto custom-scrollbar">
                  <table className="w-full min-w-[560px]">
                    <thead className={colors.tableHeader}>
                      <tr>
                        <th className={`px-4 sm:px-6 py-4 text-left text-xs font-bold ${colors.textMuted} uppercase tracking-widest`}>Öğrenci</th>
                        <th className={`px-4 sm:px-6 py-4 text-left text-xs font-bold ${colors.textMuted} uppercase tracking-widest`}>İletişim</th>
                        <th className={`px-4 sm:px-6 py-4 text-left text-xs font-bold ${colors.textMuted} uppercase tracking-widest`}>Durum</th>
                        <th className={`px-4 sm:px-6 py-4 text-left text-xs font-bold ${colors.textMuted} uppercase tracking-widest text-right`}>Detay</th>
                      </tr>
                    </thead>
                    <tbody className={`${colors.tableDivide} divide-y`}>
                      {assignedStudents.map((student) => (
                        <tr key={student.id} className={`${colors.tableRowHover} transition-colors cursor-pointer`} onClick={() => navigate(`/students/${student.id}`, { state: { student, organizationId } })}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-sm bg-gradient-to-br from-blue-500 to-indigo-600`}>
                                {student.name?.charAt(0)}{student.surname?.charAt(0)}
                              </div>
                              <div className="flex flex-col">
                                <span className={`text-sm font-bold ${colors.textPrimary}`}>{student.name} {student.surname}</span>
                                <span className={`text-[10px] font-medium text-blue-500`}>#{student.id.slice(-6)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <div className={`flex items-center gap-1.5 text-xs ${colors.textPrimary}`}>
                                <Mail className="w-3 h-3 text-zinc-400" />
                                {student.email || "-"}
                              </div>
                              <div className={`flex items-center gap-1.5 text-xs ${colors.textMuted}`}>
                                <Phone className="w-3 h-3 text-zinc-400" />
                                {student.phone || "-"}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${student.studentStatus === 'active'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                              }`}>
                              {student.studentStatus === 'active' ? 'Aktif' : student.studentStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button className={`p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors`}>
                              <Eye className={`w-4 h-4 ${colors.textTertiary}`} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === "details" && (
            <div
              className={`${colors.cardBg} backdrop-blur-sm rounded-lg border ${colors.cardBorder} p-6`}
            >
              <h3 className={`text-lg font-medium ${colors.textPrimary} mb-6`}>
                Eğitmen Detayları
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className={`font-medium ${colors.textPrimary} mb-4`}>
                    Kişisel Bilgiler
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Ad Soyad:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.name}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        E-posta:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.email}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Telefon:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.phone}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Cinsiyet:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.gender === "Male"
                          ? "Erkek"
                          : trainer.gender === "Female"
                            ? "Kadın"
                            : trainer.gender === "Other"
                              ? "Diğer"
                              : "Belirtilmemiş"}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Yaş:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.age || "Belirtilmemiş"}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className={`font-medium ${colors.textPrimary} mb-4`}>
                    Mesleki Bilgiler
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Rol:
                      </span>
                      <p className={`text-sm ${colors.textPrimary} capitalize`}>
                        {trainer.role === "trainer" ? "Eğitmen" : "Yönetici"}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Bireysel Komisyon:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.privateCommissionPercentage ??
                          trainer.trainerCommissionPercentage ??
                          0}
                        %
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Grup Komisyonu:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.groupCommissionPercentage ?? 0}%
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Durum:
                      </span>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ml-2 ${trainer.status === "active"
                          ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                      >
                        {trainer.status === "active"
                          ? "Aktif"
                          : trainer.status === "inactive"
                            ? "Pasif"
                            : "Askıda"}
                      </span>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        TC No:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.tcNo || "Belirtilmemiş"}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Kan Grubu:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.bloodType || "Belirtilmemiş"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <h4 className={`font-medium ${colors.textPrimary} mb-4`}>
                    Fiziksel Bilgiler
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Boy:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.height
                          ? `${trainer.height} cm`
                          : "Belirtilmemiş"}
                      </p>
                    </div>
                    <div>
                      <span
                        className={`text-sm font-medium ${colors.textMuted}`}
                      >
                        Kilo:
                      </span>
                      <p className={`text-sm ${colors.textPrimary}`}>
                        {trainer.weight
                          ? `${trainer.weight} kg`
                          : "Belirtilmemiş"}
                      </p>
                    </div>
                  </div>
                </div>

                {trainer.notes && (
                  <div className="md:col-span-2">
                    <h4 className={`font-medium ${colors.textPrimary} mb-4`}>
                      Notlar
                    </h4>
                    <p
                      className={`text-sm ${colors.textSecondary} ${colors.bgTertiary} p-3 rounded-lg whitespace-pre-wrap border ${colors.border}`}
                    >
                      {trainer.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* End rounded card wrapper */}
      </div>

      {/* Add Trainer Salary Modal */}
      {showSalaryModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`${colors.cardBg} backdrop-blur-sm rounded-lg shadow-xl border ${colors.cardBorder} w-full max-w-md max-h-[90vh] overflow-y-auto custom-scrollbar`}
          >
            <div
              className={`flex items-center justify-between p-4 sm:p-6 border-b ${colors.border}`}
            >
              <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
                Eğitmen Maaşı Ekle
              </h2>
              <button
                onClick={() => setShowSalaryModal(false)}
                className={`${colors.textMuted} hover:${colors.textSecondary} transition-colors`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
                  >
                    Eğitmen
                  </label>
                  <div
                    className={`px-3 py-2 border ${colors.border} rounded-md ${colors.bgTertiary} ${colors.textSecondary}`}
                  >
                    {trainer.name}
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
                  >
                    Tarih Aralığı
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={dateRange.startDate}
                      className={`w-full min-w-0 px-3 py-2 border ${colors.border} rounded-md ${colors.bgTertiary} ${colors.textSecondary}`}
                      readOnly
                    />
                    <input
                      type="date"
                      value={dateRange.endDate}
                      className={`w-full min-w-0 px-3 py-2 border ${colors.border} rounded-md ${colors.bgTertiary} ${colors.textSecondary}`}
                      readOnly
                    />
                  </div>
                </div>

                <div>
                  <p className={`text-sm ${colors.textTertiary} mb-2`}>
                    Tutar, tamamlanan seanslara göre seçilen tarih aralığı için
                    otomatik olarak hesaplanır. Tutar alanını düzenleyerek
                    değiştirebilirsiniz.
                  </p>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
                  >
                    Tutar
                  </label>
                  <div className="relative">
                    <span
                      className={`absolute left-3 top-2 ${colors.textMuted}`}
                    >
                      ₺
                    </span>
                    <input
                      type="number"
                      value={salaryData.amount}
                      onChange={(e) =>
                        setSalaryData((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                      className={`w-full pl-8 pr-3 py-2 border rounded-md ${colors.inputBg} ${colors.inputText} ${colors.inputBorder} focus:ring-2 ${colors.inputFocus} focus:border-transparent`}
                      step="0.01"
                    />
                  </div>
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
                  >
                    Tarih
                  </label>
                  <input
                    type="date"
                    value={salaryData.date}
                    onChange={(e) =>
                      setSalaryData((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className={`w-full px-3 py-2 border rounded-md ${colors.inputBg} ${colors.inputText} ${colors.inputBorder} focus:ring-2 ${colors.inputFocus} focus:border-transparent`}
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${colors.textSecondary} mb-2`}
                  >
                    Açıklama
                  </label>
                  <textarea
                    value={salaryData.description}
                    onChange={(e) =>
                      setSalaryData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className={`w-full px-3 py-2 border rounded-md ${colors.inputBg} ${colors.inputText} ${colors.inputPlaceholder} ${colors.inputBorder} focus:ring-2 ${colors.inputFocus} focus:border-transparent`}
                    rows="3"
                    placeholder="Açıklama ekleyin"
                  />
                </div>

                {salaryError && (
                  <div className="text-red-500 text-sm mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                    {salaryError}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSalaryModal(false)}
                  className={`px-4 py-2 border ${colors.btnSecondaryBorder} rounded-md ${colors.btnSecondaryText} ${colors.btnSecondaryBg} ${colors.btnSecondaryHover} transition-colors`}
                >
                  İptal
                </button>
                <button
                  onClick={handleSalarySubmit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm"
                >
                  Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`${colors.cardBg} backdrop-blur-sm rounded-lg shadow-xl border ${colors.cardBorder} w-full max-w-sm p-6 text-center`}
          >
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className={`text-lg font-medium ${colors.textPrimary} mb-2`}>
              İşlem Başarılı
            </h3>
            <p className={`${colors.textMuted} mb-6`}>
              Eğitmen maaş ödemesi başarıyla oluşturuldu.
            </p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
            >
              Tamam
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
